import React, { createContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import { Box, Button, Grid } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { ItemServices } from './ItemServices';
import dayjs from 'dayjs';
import Loading from '../../../../components/Loader/Loading';
import ItemForm from './ItemForm';
import { SectionServices } from '../Section/SectionServices';


export const ItemDataContext = createContext();
const AddEditSectionItem = () => {
    const menuDispatch = useDispatch();
    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: 'home-screen'
        });
    }, []);
    const {id} = useParams();
    const navigate = useNavigate();
    const [pageTitle, setPageTitle] = useState('Add Section Item');
    useEffect(()=>{
        if(id>0){
            setPageTitle('Edit Section Item');
            fetchItemData();
        }else{
            setPageTitle('Add Section Item');
        }
    },[id])
    const initialState = {
        title: '',
        subtitle: '',
        description: '',
        displayOptions: {
            showTitle: true,
            titlePosition: 'left',
            showSubTitle: true,
            subTitlePosition: 'left'
        },
        imageUrl: null,
        thumbnailUrl: null,
        iconUrl: null,
        actionType: '',
        actionData: {},
        badgeText: '',
        badgeColor: '#FF6B35',
        badgePosition: 'top-right',
        position: '',
        cardBackgroundColor: null,
        cardBorderColor: null,
        aspectRatio: '1:1',
        isActive: true,
        startDateTime: null,
        endDateTime: null,
        serviceRelations: []
    };

    const [itemData, setItemData] = useState(initialState);
    const [sectionData, setSectionData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const ItemFormMethod = useForm({
        mode: 'onChange',
        defaultValues: initialState
    });

    const { handleSubmit, reset, setValue, watch, formState: { isDirty } } = ItemFormMethod;
    const fetchItemData = async () => {
        setIsLoading(true);
        try {
            const response = await ItemServices.getItemById(id);
            if (response.success == '1') {
                const data = response.data;
                const requiredData = {
                    sectionId: data.sectionId || '',
                    title: data.title || '',
                    subtitle: data.subtitle || '',
                    description: data.description || '',
                    displayOptions: data.displayOptions || initialState.displayOptions,
                    imageUrl: data.imageUrl || null,
                    thumbnailUrl: data.thumbnailUrl || null,
                    iconUrl: data.iconUrl || null,
                    actionType: data.actionType || '',
                    actionData: data.actionData || {},
                    badgeText: data.badgeText || '',
                    badgeColor: data.badgeColor || '#FF6B35',
                    badgePosition: data.badgePosition || 'top-right',
                    position: data.position || '',
                    cardBackgroundColor: data.cardBackgroundColor || null,
                    cardBorderColor: data.cardBorderColor || null,
                    aspectRatio: data.aspectRatio || '1:1',
                    isActive: data.isActive ?? true,
                    startDateTime: data.startDateTime ? dayjs(data.startDateTime).format('YYYY-MM-DDTHH:mm') : null,
                    endDateTime: data.endDateTime ? dayjs(data.endDateTime).format('YYYY-MM-DDTHH:mm') : null,
                    // serviceRelations: data.serviceRelations || []
                    serviceRelations: data.serviceRelations.map(relation => ({
                        id: relation.id,
                        servicesMasterId: relation.service.parentServiceId,
                        servicesChildId: relation.service.id,
                        categoryId: relation.category.parentId,
                        subCategoryId: relation.category.id,
                        categoryTitle: relation.category.parentCategory.title,
                        subCategoryTitle: relation.category.title,
                        masterServiceTitle: relation.service.parentService.title,
                        childServiceTitle: relation.service.title
                    })) || []
                };
                setItemData(data);
                reset(requiredData, { isDirty: false });
            }
        } catch (error) {
            console.log("error", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch item details',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const [sectionList, setSectionList] = useState([]);
    const fetchSectionList = async () => {
        try{
            const response = await SectionServices.fetchSectionList();
            if(response.success == '1'){
                const sections = response.data.list || [];
                setSectionList(sections);
            }else if(response.success == '0'){
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to fetch sections',
                });
                setSectionList([]);
            }

        } catch(error){
            console.log("error", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch section details',
            });
        }
    }

    const sectionId = watch('sectionId');

    const onSubmit = async (data) => {
        setIsSubmitting(true);

        const modifiedData = {
            sectionId: parseInt(sectionId),
            title: data.title,
            subtitle: data.subtitle || null,
            description: data.description || null,
            displayOptions: data.displayOptions,
            imageUrl: data.imageUrl || null,
            thumbnailUrl: data.thumbnailUrl || null,
            iconUrl: data.iconUrl || null,
            actionType: data.actionType,
            actionData: data.actionType !== 'none' ? data.actionData : null,
            badgeText: data.badgeText || null,
            badgeColor: data.badgeText ? data.badgeColor : null,
            badgePosition: data.badgeText ? data.badgePosition : null,
            position: data.position ? parseInt(data.position) : 0,
            cardBackgroundColor: data.cardBackgroundColor || null,
            cardBorderColor: data.cardBorderColor || null,
            aspectRatio: data.aspectRatio,
            isActive: data.isActive,
            startDateTime: data.startDateTime ? new Date(data.startDateTime).toISOString() : null,
            endDateTime: data.endDateTime ? new Date(data.endDateTime).toISOString() : null,
            serviceRelations: data.serviceRelations.map(relation => ({
                serviceCategoryId: relation.subCategoryId,
                servicesMasterId: relation.servicesChildId || null,
                id: relation.id || 0
            }))
        };

        try {
            if (id>0) {
                const updateResponse = await ItemServices.updateItem(id, modifiedData);
                if (updateResponse.success == '1') {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Item updated successfully',
                    });
                    navigate(`/customer-app/home-screen/section-item/view/${id}`);
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: updateResponse.message || 'Item update failed',
                    });
                }
            } else {
                const createResponse = await ItemServices.addItem(modifiedData);
                if (createResponse.success == '1') {
                    const result = await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Item created successfully',
                        showCancelButton: true,
                        confirmButtonText: 'Add New',
                        cancelButtonText: 'Show List',
                        confirmButtonColor: 'green',
                        cancelButtonColor: '#5856d6',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                    if (result.isConfirmed) {
                        reset(initialState);
                        setItemData(initialState);
                    }
                    if (result.dismiss === Swal.DismissReason.cancel) {
                        navigate(`/customer-app/home-screen/section-item`);
                    }
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: createResponse.message || 'Item creation failed',
                    });
                }
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: id ? 'Item update failed' : 'Item creation failed',
                timer: 1500
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        fetchSectionList();
    }, []);
  return (
    <>
      <PageHeaderLayout headerTitle={pageTitle}>
            <Button sx={{textTransform:'none'}} size='small' variant='contained' startIcon={<ArrowBack/>} onClick={()=>navigate('/customer-app/home-screen/section-item')}>
                Show Section Items
            </Button>
        </PageHeaderLayout>
        <ItemDataContext.Provider value={{ itemData, setItemData, isSubmitting, onSubmit, sectionList }}>
            {(isLoading || isSubmitting) && <Loading />}
            {/* <PageHeaderLayout headerTitle={id ? `Edit Item - ${sectionData?.title || 'Section'}` : `Add New Item - ${sectionData?.title || 'Section'}`}>
                <Button variant='contained' onClick={() => navigate(-1)} sx={{ height: "30px", marginTop: "10px" }} startIcon={<ArrowBack />}>
                    Back
                </Button>
            </PageHeaderLayout> */}
            <FormProvider {...ItemFormMethod}>
                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <ItemForm sectionData={sectionData} />
                        <Grid item xs={12} md={4}>
                            <Box sx={{ position: 'sticky', top: 20 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    sx={{ mb: 1 }}
                                    disabled={!isDirty || isSubmitting}
                                >
                                    {id>0 ? 'Update Item' : 'Create Item'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => navigate(-1)}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </FormProvider>
        </ItemDataContext.Provider>
    </>
  )
}

export default AddEditSectionItem
